import {PlaceDetails} from './PlaceDetails';
import { contactList } from "./ResidentStudio";
import { cityLocale, t, setCityDirection } from "../i18n";
import { cityPath, cityPathForLanguage, sitePath } from "../urls";
import { CityApi } from "../api/client";
import type {
  CityRuntimeResponse,
  HouseData,
  PlotData,
  WorldResponse,
} from "../api/types";
import { cityIcon } from "./icons";
import { CityMusicPlayer } from "./CityMusicPlayer";

export type EconomyTab = "assets" | "wallet" | "properties";

export type CityUIActions = {
  onSearch: (query: string) => void;
  onDistrict: (district: string) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetCamera: () => void;
  onTimeChange: (hour: number) => void;
  onPurchasePlot: (plot: PlotData) => void;
  onPurchaseListing: (plot: PlotData) => void;
  onLandMarket: () => void;
  onDesignPlot: (plot: PlotData) => void;
  onOpenHouse: (house: HouseData) => void;
  onManageHouse: (house: HouseData) => void;
  onManageResident: (house: HouseData) => void;
  onEconomy: (tab?: EconomyTab) => void;
  onDirectory: () => void;
};

const DISTRICTS: Record<string, string> = {
  all: "همه",
  technology: "فناوری",
  fashion: "مد و پوشاک",
  food: "غذا و نوشیدنی",
  services: "خدمات",
  entertainment: "سرگرمی",
  business: "کسب‌وکار",
};

function districtLabel(value?: string): string {
  if (!value) return "سایر";
  return DISTRICTS[value] ?? value;
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.content ?? "";
}

export class CityUI {
  private readonly root = document.createElement("div");
  private readonly detailBackdrop = document.createElement("div");
  private readonly toastNode = document.createElement("div");
  private readonly dayOverlay = document.createElement("div");
  private readonly musicPlayer: CityMusicPlayer;
  private readonly citySelect = document.createElement("select");
  private activePlace?:PlaceDetails;
  private toastTimer: number | null = null;
  private world: WorldResponse | null = null;
  private readonly abort = new AbortController();

  constructor(
    host: HTMLElement,
    private readonly runtime: CityRuntimeResponse,
    private readonly api: CityApi,
    private readonly actions: CityUIActions,
  ) {
    this.root.className = "city-ui city-legacy-ui city-atlas";
    setCityDirection(this.root);
    this.musicPlayer = new CityMusicPlayer(
      this.runtime.city?.playlist ?? [],
      Number(this.runtime.city?.playlistVolume ?? 0.35),
    );

    this.dayOverlay.className = "city-daylight-overlay";
    host.appendChild(this.dayOverlay);

    const topbar = document.createElement("header");
    topbar.className = "topbar city-unified-nav";

    const brand = document.createElement("a");
    brand.className = "brand";
    brand.href = cityPath();
    const mark = document.createElement("span");
    mark.className = "brand-mark";
    mark.append(cityIcon('store'));
    const brandText = document.createElement("span");
    brandText.className = "brand-text";
    const brandSub = document.createElement("span");
    brandSub.className = "brand-sub";
    // City and country are each rendered exactly once: تهران / ایران.
    brandSub.textContent = this.runtime.city?.country.name || "Website City";
    const brandName = document.createElement('strong');
    brandName.className = 'brand-name';
    brandName.textContent = this.runtime.city?.name || 'Website City';
    brandText.append(brandName, brandSub);
    brand.append(mark, brandText);
    brand.setAttribute(
      "aria-label",
      this.runtime.city
        ? `${this.runtime.city.name} · ${this.runtime.city.country.name}`
        : "Website City",
    );

    const searchWrap = document.createElement("div");
    searchWrap.className = "topbar-center";
    const searchBox = document.createElement("div");
    searchBox.className = "search";
    const searchInner = document.createElement("label");
    searchInner.className = "search-input";
    searchInner.setAttribute("aria-label", "جستجوی فروشگاه‌ها");
    const searchIcon = document.createElement("span");
    searchIcon.append(cityIcon('search'));
    const search = document.createElement("input");
    search.type = "search";
    search.id = "city-store-search";
    search.name = "city_store_search";
    search.placeholder = "جستجوی فروشگاه‌ها…";
    search.autocomplete = "off";
    search.addEventListener("input", () => this.actions.onSearch(search.value));
    searchInner.append(searchIcon, search);
    searchBox.appendChild(searchInner);
    searchWrap.appendChild(searchBox);

    const actionbar = document.createElement("div");
    actionbar.className = "topbar-actions";
    actionbar.id = "city-navigation";
    actionbar.setAttribute("role", "navigation");
    actionbar.setAttribute("aria-label", "ناوبری شهر");

    const settingsPanel = document.createElement("aside");
    settingsPanel.className = "city-settings-panel";
    settingsPanel.id = "city-settings";
    settingsPanel.hidden = true;
    settingsPanel.setAttribute("aria-label", "تنظیمات و حساب کاربری");
    const settingsHeader = document.createElement("header");
    const settingsTitle = document.createElement("strong");
    settingsTitle.textContent = "تنظیمات شهر";
    const closeSettings = this.actionButton("بستن تنظیمات", () => setSettingsOpen(false));
    closeSettings.replaceChildren(cityIcon("close"));
    closeSettings.setAttribute("aria-label", "بستن تنظیمات");
    settingsHeader.append(settingsTitle, closeSettings);
    settingsPanel.append(settingsHeader);
    const settingsTriggers: HTMLButtonElement[] = [];
    const setSettingsOpen = (open: boolean) => {
      if (open) this.closeDetail();
      settingsPanel.hidden = !open;
      settingsTriggers.forEach(button => button.setAttribute("aria-expanded", String(open)));
      if (open) closeSettings.focus();
    };
    const settingsTrigger = (label: string, icon: string) => {
      const button = this.actionButton(label, () => setSettingsOpen(settingsPanel.hidden));
      button.setAttribute("aria-controls", "city-settings");
      button.setAttribute("aria-expanded", "false");
      button.replaceChildren(cityIcon(icon), document.createTextNode(label));
      settingsTriggers.push(button);
      return button;
    };
    const addToSettings = (...nodes: HTMLElement[]) => {
      nodes.forEach(node => settingsPanel.appendChild(node));
    };
    addToSettings(
      this.linkButton("بازی سایبر رانر", "/play/"),
      this.linkButton("فروشگاه اصلی", sitePath()),
    );

    if (this.runtime.viewer.authenticated) {
      if (this.runtime.viewer.isCustomer) {
        addToSettings(
          this.actionButton("املاک من", () => this.actions.onEconomy("properties")),
          this.linkButton("مغازه من", `${cityPath("my-shop/")}?city=${encodeURIComponent(this.runtime.city?.slug ?? '')}`),
        );
      }

      if (this.runtime.viewer.isAdmin) {
        addToSettings(
          this.linkButton(
            "پنل ادمین",
            `${cityPath("admin/")}?city=${encodeURIComponent(this.runtime.city?.slug ?? "")}`,
          ),
        );
        actionbar.append(
          this.linkButton(
            "ویرایشگر شهر",
            `${cityPath("editor/")}?city=${encodeURIComponent(this.runtime.city?.slug ?? "")}`,
            "btn-editor",
          ),
        );
      }

      const account = settingsTrigger("حساب کاربری", "users");
      account.className = "city-account city-profile-button";
      account.setAttribute("aria-label", "حساب کاربری");
      const name = document.createElement("span");
      name.className = "city-account-name";
      name.translate = false;
      name.title = this.runtime.viewer.displayName;
      name.textContent = this.runtime.viewer.displayName || "حساب کاربری";
      const profileBalance = this.runtime.viewer.isCustomer
        ? document.createElement("button") : document.createElement("span");
      profileBalance.className = "city-profile-balance";
      const profileCoin = document.createElement("span");
      profileCoin.className = "city-coin";
      profileCoin.append(cityIcon("star"));
      const profileAmount = document.createElement("span");
      profileAmount.className = "city-profile-balance-amount";
      profileAmount.textContent = Number(this.runtime.viewer.walletBalance || 0).toLocaleString(cityLocale());
      profileBalance.append(profileCoin, profileAmount);
      profileBalance.title = `موجودی ${profileAmount.textContent} KScoin`;
      if (profileBalance instanceof HTMLButtonElement) {
        profileBalance.type = "button";
        profileBalance.setAttribute("aria-label", `${profileAmount.textContent} KScoin · ${t("Wallet", "کیف پول")}`);
        profileBalance.addEventListener("click", () => this.actions.onEconomy("wallet"));
      }
      account.title = this.runtime.viewer.displayName || "حساب کاربری";

      if (this.runtime.viewer.isCustomer) {
        // One shared balance element for all breakpoints. A duplicate wallet
        // widget previously resurfaced on phones because of !important CSS.
        addToSettings(this.actionButton("کیف پول", () => this.actions.onEconomy("wallet")));
      }

      const logout = document.createElement("form");
      logout.method = "post";
      logout.action = "/customer/logout/";
      const csrf = document.createElement("input");
      csrf.type = "hidden";
      csrf.name = "csrfmiddlewaretoken";
      csrf.value = csrfToken();
      const logoutButton = document.createElement("button");
      logoutButton.type = "submit";
      logoutButton.className = "btn";
      logoutButton.textContent = "خروج از حساب";
      logout.append(csrf, logoutButton);
      account.replaceChildren(cityIcon("users"), name, cityIcon("chevron-down"));
      actionbar.append(account, profileBalance);
      addToSettings(logout);
    } else {
      const account = document.createElement("div");
      account.className = "city-account";
      account.append(
        this.linkButton("ورود", `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`),
        this.linkButton("ثبت‌نام", `${sitePath("customer/register/")}?next=${encodeURIComponent(cityPath())}`),
      );
      actionbar.appendChild(account);
    }

    const economy = this.actionButton(
      "خرید زمین",
      () => this.actions.onLandMarket(),
      "btn-economy",
    );
    actionbar.appendChild(economy);

    const directory = this.actionButton(
      "فهرست فروشگاه‌ها",
      () => this.actions.onDirectory(),
      "btn city-directory-button",
    );
    directory.title = "فهرست فروشگاه‌ها";
    directory.setAttribute("aria-label", "فهرست فروشگاه‌ها");
    actionbar.appendChild(directory);

    const timeControl = document.createElement("div");
    timeControl.className = "time-control";
    const dayButton = document.createElement("button");
    dayButton.type = "button";
    dayButton.className = "icon-btn";
    dayButton.replaceChildren(cityIcon("sun"));
    dayButton.title = "زمان روز";
    const timeSlider = document.createElement("input");
    timeSlider.className = "time-slider";
    timeSlider.type = "range";
    timeSlider.id = "city-daylight-hour";
    timeSlider.name = "city_daylight_hour";
    timeSlider.min = "0";
    timeSlider.max = "24";
    timeSlider.step = "1";
    timeSlider.value = String(new Date().getHours());
    timeSlider.setAttribute("aria-label", "زمان روز");
    timeSlider.addEventListener("input", () => {
      const hour = Number(timeSlider.value);
      this.applyDaylight(hour);
      dayButton.replaceChildren(cityIcon(hour >= 7 && hour < 18 ? "sun" : "moon"));
    });
    dayButton.addEventListener("click", () => {
      const current = Number(timeSlider.value);
      timeSlider.value = current >= 7 && current < 18 ? "22" : "12";
      timeSlider.dispatchEvent(new Event("input"));
    });
    timeControl.append(dayButton, timeSlider);
    addToSettings(timeControl);

    topbar.append(brand, searchWrap, actionbar);

    this.citySelect.className = "city-topbar-city-select";
    this.citySelect.id = "city-travel-city";
    this.citySelect.name = "city_travel_city";
    this.citySelect.setAttribute("aria-label", "انتخاب شهر");
    const fallback = document.createElement("option");
    fallback.value = this.runtime.city?.slug ?? "";
    fallback.textContent = this.runtime.city
      ? `${this.runtime.city.name} · ${this.runtime.city.country.name}`
      : "انتخاب شهر";
    this.citySelect.appendChild(fallback);
    this.citySelect.addEventListener("change", () => {
      const slug = this.citySelect.value;
      if (!slug) return;
      const language = this.citySelect.selectedOptions[0]?.dataset.language ?? "";
      this.navigateCity(slug, language);
    });

    const travelDock = document.createElement("div");
    travelDock.className = "city-travel-dock";
    travelDock.setAttribute("aria-label", "سفر به شهر دیگر");
    travelDock.append(cityIcon("external"), this.citySelect);

    // All cities can explicitly change UI language; the domain default
    // remains unchanged when a visitor has not selected one.
    const headerUtilities = document.createElement("div");
    headerUtilities.className = "city-header-utilities";
    {
      const picker = document.createElement("div");
      picker.className = "city-language-picker";
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "city-language-toggle";
      toggle.setAttribute("aria-label", t("Change city language", "تغییر زبان شهر"));
      toggle.title = t("Change city language", "تغییر زبان شهر");
      toggle.setAttribute("aria-haspopup", "true");
      toggle.setAttribute("aria-expanded", "false");
      const active = (document.body.dataset.cityLanguage || "en").split("-", 1)[0].toLowerCase();
      const toggleText = document.createElement("span");
      toggleText.className = "city-language-button-label";
      toggleText.textContent = t("Language", "زبان");
      toggle.append(cityIcon("languages"), toggleText);

      const menu = document.createElement("div");
      menu.className = "city-language-menu";
      menu.setAttribute("role", "menu");
      menu.hidden = true;
      const close = () => {
        menu.hidden = true;
        toggle.setAttribute("aria-expanded", "false");
      };
      toggle.addEventListener("click", () => {
        menu.hidden = !menu.hidden;
        toggle.setAttribute("aria-expanded", String(!menu.hidden));
      });
      for (const [code, nativeName] of [["fa", "فارسی"], ["en", "English"], ["de", "Deutsch"], ["fi", "Suomi"]]) {
        const option = document.createElement("button");
        option.type = "button";
        option.className = "city-language-option";
        option.setAttribute("role", "menuitemradio");
        option.setAttribute("aria-checked", String(code === active));
        option.textContent = `${code.toUpperCase()} · ${nativeName}`;
        option.addEventListener("click", () => {
          close();
          if (code === active || !this.runtime.city) return;
          // Preserve the current subpage and city, changing only UI language.
          const base = cityPath();
          const suffix = location.pathname.startsWith(base)
            ? location.pathname.slice(base.length) : "";
          const target = new URL(cityPathForLanguage(code, suffix), location.origin);
          target.search = location.search;
          target.searchParams.set("city", this.runtime.city.slug);
          target.searchParams.set("ui_lang", code);
          target.searchParams.delete("lang");
          location.assign(target.href);
        });
        menu.append(option);
      }
      document.addEventListener("pointerdown", event => {
        if (!picker.contains(event.target as Node)) close();
      }, { signal: this.abort.signal });
      document.addEventListener("keydown", event => {
        if (event.key === "Escape" && !menu.hidden) {
          close();
          toggle.focus();
        }
      }, { signal: this.abort.signal });
      picker.append(toggle, menu);
      searchWrap.append(picker);
    }
    headerUtilities.append(this.musicPlayer.root);
    actionbar.append(headerUtilities);
    // Phone-only return to the existing interactive world map, not a
    // duplicate country selector. Without ?city= the world entry opens.
    const worldReturn = document.createElement("a");
    worldReturn.className = "city-world-return";
    worldReturn.href = cityPath();
    worldReturn.setAttribute("aria-label", t("World map", "نقشه جهان"));
    worldReturn.title = t("Return to world map", "بازگشت به نقشه جهان");
    const worldLabel = document.createElement("span");
    worldLabel.className = "city-world-return-label";
    worldLabel.textContent = t("World map", "نقشه جهان");
    worldReturn.append(cityIcon("map-pin"), worldLabel);
    actionbar.append(worldReturn);
    document.addEventListener("pointerdown", event => {
      const target = event.target as Node;
      if (!settingsPanel.contains(target) && !settingsTriggers.some(button => button.contains(target))) {
        setSettingsOpen(false);
      }
    }, {signal: this.abort.signal});
    document.addEventListener("keydown", event => {
      if (event.key !== "Escape" || document.querySelector("dialog.city-place-viewer[open]")) return;
      if (!this.detailBackdrop.hidden) this.closeDetail();
      if (!settingsPanel.hidden) {
        setSettingsOpen(false);
        settingsTriggers[0]?.focus();
      }
    }, {signal: this.abort.signal});

    this.detailBackdrop.className = "panel-backdrop";
    this.detailBackdrop.hidden = true;
    this.detailBackdrop.addEventListener("pointerdown", (event) => {
      if (event.target === this.detailBackdrop) this.closeDetail();
    });

    this.toastNode.className = "city-toast";
    this.toastNode.hidden = true;

    const rail = document.createElement('nav');
    rail.className = 'city-rail';
    rail.setAttribute('aria-label', 'ناوبری اصلی شهر');
    const railItem = (node: HTMLElement, icon: string) => {
      const label = document.createElement('span');
      label.textContent = node.textContent;
      node.title = node.textContent || "";
      node.replaceChildren(cityIcon(icon), label);
      rail.append(node);
    };
    const mapButton = this.actionButton('نقشه شهر', this.actions.onResetCamera);
    mapButton.classList.add('is-active');
    railItem(mapButton, 'pin');
    railItem(economy, 'inventory');
    for (const [label, icon] of [['املاک من','dashboard'],['ویرایشگر شهر','customizer'],['پنل ادمین','settings']]) {
      const node = [...actionbar.querySelectorAll<HTMLElement>('button,a'), ...settingsPanel.querySelectorAll<HTMLElement>('button,a')].find(item => item.textContent === label);
      if (node) railItem(node, icon);
    }
    railItem(directory, 'categories');
    railItem(settingsTrigger('تنظیمات', 'settings'), 'settings');
    actionbar.prepend(travelDock);
    this.root.append(
      topbar,
      rail,
      settingsPanel,
      this.detailBackdrop,
      this.toastNode,
    );
    host.appendChild(this.root);

    this.applyDaylight(Number(timeSlider.value));
    this.syncNavbarHeight(topbar);
    void this.loadWorld();
  }

  selectHouse(house: HouseData): void {
    const panel = document.createElement("aside");
    setCityDirection(panel);
    panel.className = "store-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", `اطلاعات فروشگاه ${house.businessName}`);

    const header = document.createElement("header");
    header.className = "panel-header";
    const cover = document.createElement("div");
    cover.className = "panel-cover";
    const logo = document.createElement("div");
    logo.className = "panel-logo";
    logo.translate = false;
    logo.textContent = house.businessName.slice(0, 2).toUpperCase();
    cover.appendChild(logo);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "icon-btn panel-close";
    close.textContent = "×";
    close.setAttribute("aria-label", "بستن اطلاعات فروشگاه");
    close.addEventListener("click", () => this.closeDetail());
    header.append(cover, close);

    const body = document.createElement("div");
    body.className = "panel-body";
    const titleRow = document.createElement("div");
    titleRow.className = "panel-title-row";
    const title = document.createElement("h2");
    title.translate = false;
    title.dir = "auto";
    title.textContent = house.businessName;
    const status = document.createElement("span");
    status.className = "tier-badge";
    status.textContent = house.published ? "فعال" : "پیش‌نویس";
    titleRow.append(title, status);

    const chips = document.createElement("div");
    chips.className = "panel-chips";
    const category = document.createElement("span");
    category.className = "chip chip-static";
    category.textContent = districtLabel(house.district);
    chips.appendChild(category);
    if (house.canManage) {
      const owned = document.createElement("span");
      owned.className = "chip chip-static chip-owned";
      owned.textContent = "مغازهٔ شما";
      chips.appendChild(owned);
    }

    const description = document.createElement("p");
    description.className = "panel-desc";
    const store = house.store ?? {};
    description.textContent =
      (typeof store.description === "string" && store.description) ||
      (typeof store.bio === "string" && store.bio) ||
      "این مغازه بخشی از شهر وب‌سایت‌هاست.";
    if (store.description || store.bio) description.translate = false;
    description.dir = "auto";

    const meta = document.createElement("dl");
    meta.className = "panel-meta";
    const activity = document.createElement("div");
    const activityLabel = document.createElement("dt");
    activityLabel.textContent = "وضعیت فعالیت";
    const activityValue = document.createElement("dd");
    activityValue.textContent = house.openFrom && house.openUntil
      ? `${house.openFrom}–${house.openUntil}` : house.published ? "فعال" : "پیش‌نویس";
    activity.append(activityLabel, activityValue);
    const owner = document.createElement("div");
    const ownerLabel = document.createElement("dt");
    ownerLabel.textContent = "مالک";
    const ownerValue = document.createElement("dd");
    ownerValue.translate = false;
    ownerValue.dir = "auto";
    ownerValue.textContent = house.ownerName || "—";
    owner.append(ownerLabel, ownerValue);
    meta.append(activity, owner);

    const actions = document.createElement("div");
    actions.className = "panel-actions";
    if ((house.floorPlans?.length ?? 0) > 0) {
      actions.appendChild(this.panelButton(
        "ورود به مغازه",
        () => this.actions.onOpenHouse(house),
        true,
      ));
    }

    if (house.canManage) {
      actions.appendChild(this.panelButton(
        "طراحی مغازه",
        () => this.actions.onManageHouse(house),
      ));
    }

    if (house.canManage) actions.appendChild(this.panelButton("اطلاعات تماس", () => this.actions.onManageResident(house)));
    body.append(titleRow, chips, description, meta, contactList(house.contacts?.business), actions);
    panel.append(header, body);
    this.openDetail(panel);
  }

  selectResident(house:HouseData):void {
    const panel=document.createElement("aside");setCityDirection(panel);panel.className="store-panel resident-profile";panel.setAttribute("role","dialog");panel.setAttribute("aria-label","اطلاعات مالک");
    const body=document.createElement("div");body.className="panel-body";
    const close=document.createElement("button");close.type="button";close.className="icon-btn panel-close";close.textContent="×";close.setAttribute("aria-label","بستن");close.onclick=()=>this.closeDetail();
    const name=document.createElement("h2");name.dir="auto";name.textContent=house.ownerName??"مالک مغازه";if(house.ownerName)name.translate=false;
    const status=document.createElement("p");status.textContent=house.presence?.online?"آنلاین در شهر":"آفلاین";
    const shop=document.createElement("p");shop.dir="auto";shop.textContent=house.businessName;shop.translate=false;
    body.append(close,name,status,shop,contactList(house.contacts?.owner));
    if (house.canManage) body.append(this.panelButton("ویرایش اطلاعات تماس",()=>this.actions.onManageResident(house)));
    panel.append(body);this.openDetail(panel);
  }

  selectPlot(plot: PlotData): void {
    const panel = document.createElement("aside");
    setCityDirection(panel);
    panel.className = "store-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", plot.name);

    const header = document.createElement("header");
    header.className = "panel-header";
    const cover = document.createElement("div");
    cover.className = "panel-cover";
    const logo = document.createElement("div");
    logo.className = "panel-logo";
    logo.textContent = "زمین";
    cover.appendChild(logo);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "icon-btn panel-close";
    close.textContent = "×";
    close.setAttribute("aria-label", "بستن");
    close.addEventListener("click", () => this.closeDetail());
    header.append(cover, close);

    const body = document.createElement("div");
    body.className = "panel-body";
    const title = document.createElement("h2");
    title.textContent = plot.name;
    title.dir = "auto";
    const chips = document.createElement("div");
    chips.className = "panel-chips";
    const category = document.createElement("span");
    category.className = "chip chip-static";
    category.textContent = districtLabel(plot.district);
    const size = document.createElement("span");
    size.className = "chip chip-static";
    size.textContent = `${plot.width}×${plot.depth}`;
    chips.append(category, size);

    const description = document.createElement("p");
    description.className = "panel-desc";
    description.textContent =
      plot.description || "قطعه‌ای برای ساخت یک کسب‌وکار در شهر.";
    description.dir = "auto";

    const price = document.createElement("div");
    price.className = "city-detail__price";
    price.textContent = plot.forSale
      ? `${Number(plot.price).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`
      : plot.ownedByViewer
        ? "زمین شما"
        : plot.listing?.isListed
          ? `بازفروش: ${Number(plot.listing.buyerTotal).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`
          : plot.reserved ? "رزرو شده" : "فروخته شده";

    const actions = document.createElement("div");
    actions.className = "panel-actions";
    if (
      plot.forSale &&
      this.runtime.release.landPurchase &&
      this.runtime.viewer.isCustomer
    ) {
      actions.appendChild(this.panelButton(
        "خرید زمین",
        () => this.actions.onPurchasePlot(plot),
        true,
      ));
    } else if (plot.forSale && !this.runtime.viewer.authenticated) {
      const login = document.createElement("a");
      login.className = "btn btn-primary";
      login.href = `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`;
      login.textContent = "ورود برای خرید";
      actions.appendChild(login);
    }

    if (
      plot.listing?.isListed &&
      !plot.listing.isViewerSeller &&
      this.runtime.release.landResale &&
      this.runtime.viewer.isCustomer
    ) {
      actions.appendChild(this.panelButton(
        "خرید از مالک",
        () => this.actions.onPurchaseListing(plot),
        true,
      ));
    } else if (
      plot.listing?.isListed &&
      !plot.listing.isViewerSeller &&
      !this.runtime.viewer.authenticated
    ) {
      const login = document.createElement("a");
      login.className = "btn btn-primary";
      login.href = `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`;
      login.textContent = "ورود برای خرید";
      actions.appendChild(login);
    }

    if (plot.ownedByViewer) {
      actions.appendChild(this.panelButton(
        "طراحی مغازه",
        () => this.actions.onDesignPlot(plot),
        true,
      ));
      actions.appendChild(this.panelButton(
        "مدیریت ملک",
        () => this.actions.onEconomy("properties"),
      ));
    }

    body.append(title, chips, description, price, actions);
    panel.append(header, body);
    this.openDetail(panel);
  }

  showToast(
    message: string,
    kind: "info" | "success" | "error" = "info",
  ): void {
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastNode.hidden = false;
    this.toastNode.className = `city-toast city-toast--${kind}`;
    this.toastNode.textContent = message;
    this.toastTimer = window.setTimeout(() => {
      this.toastNode.hidden = true;
      this.toastTimer = null;
    }, 3600);
  }

  updateBalance(balance: string): void {
    this.runtime.viewer.walletBalance = balance;
    const displayed = Number(balance).toLocaleString(cityLocale());
    const wallet = this.root.querySelector<HTMLElement>(".city-profile-balance");
    const amount = wallet?.querySelector<HTMLElement>(".city-profile-balance-amount");
    if (amount) amount.textContent = displayed;
    if (wallet) {
      wallet.title = `موجودی ${displayed} KScoin`;
      if (wallet instanceof HTMLButtonElement) {
        wallet.setAttribute("aria-label", `${displayed} KScoin · ${t("Wallet", "کیف پول")}`);
      }
    }
  }

  setBusy(busy: boolean): void {
    this.root.classList.toggle("is-busy", busy);
    this.root.setAttribute('aria-busy', String(busy));
  }

  closeDetail(): void {
    this.activePlace?.destroy();this.activePlace=undefined;
    this.detailBackdrop.hidden = true;
    this.detailBackdrop.replaceChildren();
    this.root.classList.remove("has-selection");
    this.detailBackdrop.classList.remove("is-expanded");
  }

  destroy(): void {
    this.activePlace?.destroy();
    this.abort.abort();
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.musicPlayer.destroy();
    this.dayOverlay.remove();
    this.root.remove();
    document.documentElement.style.removeProperty("--city-navbar-height");
  }

  showPlace(objectId:string):void{
    const detail=new PlaceDetails(this.api,()=>this.closeDetail());
    this.openDetail(detail.element);this.activePlace=detail;void detail.load(objectId);
  }

  private openDetail(panel: HTMLElement): void {
    this.activePlace?.destroy();this.activePlace=undefined;
    // A docked inspector leaves the map usable; it is not a modal dialog.
    const expand=document.createElement('button');expand.type='button';expand.className='city-inspector-expand';expand.textContent=t('Expand panel','نمایش کامل');expand.setAttribute('aria-expanded','false');
    expand.onclick=()=>{const full=this.detailBackdrop.classList.toggle('is-expanded');expand.setAttribute('aria-expanded',String(full));expand.textContent=full?t('Compact view','نمایش فشرده'):t('Expand panel','نمایش کامل');};
    panel.prepend(expand);this.detailBackdrop.classList.remove('is-expanded');
    panel.setAttribute("role", "region");
    panel.removeAttribute("aria-modal");
    this.detailBackdrop.replaceChildren(panel);
    this.detailBackdrop.hidden = false;
    this.root.classList.add("has-selection");
  }

  private async loadWorld(): Promise<void> {
    try {
      this.world = await this.api.world();
      this.citySelect.replaceChildren();
      for (const country of this.world.countries) {
        const group = document.createElement("optgroup");
        group.label = country.name;
        for (const city of country.cities) {
          const option = document.createElement("option");
          option.value = city.slug;
          option.dataset.language = city.language.code;
          option.textContent = `${city.name} · ${country.name}`;
          option.selected = city.slug === this.runtime.city?.slug;
          group.appendChild(option);
        }
        this.citySelect.appendChild(group);
      }

    } catch {
      // Current-city controls remain usable even if world discovery fails.
    }
  }

  private navigateCity(slug: string, language: string): void {
    try { sessionStorage.setItem("city-entry-target", slug); } catch { /* optional */ }
    // Drop the old city's UI-language override when visiting another city.
    location.href = `${cityPathForLanguage(language)}?city=${encodeURIComponent(slug)}`;
  }

  private applyDaylight(hour: number): void {
    const distanceFromNoon = Math.abs(12 - hour) / 12;
    this.dayOverlay.style.opacity = String(
      Math.max(0, Math.min(0.42, distanceFromNoon * 0.42)),
    );
    this.actions.onTimeChange(hour);
  }

  private syncNavbarHeight(topbar: HTMLElement): void {
    const update = () => {
      document.documentElement.style.setProperty(
        "--city-navbar-height",
        `${topbar.getBoundingClientRect().height}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(topbar);
    this.root.addEventListener("DOMNodeRemoved", () => observer.disconnect(), {
      once: true,
    });
  }

  private actionButton(
    text: string,
    handler: () => void,
    extraClass = "",
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `btn ${extraClass}`.trim();
    button.textContent = text;
    button.addEventListener("click", handler);
    return button;
  }

  private linkButton(
    text: string,
    href: string,
    extraClass = "",
  ): HTMLAnchorElement {
    const link = document.createElement("a");
    link.className = `btn ${extraClass}`.trim();
    link.href = href;
    link.textContent = text;
    return link;
  }

  private panelButton(
    text: string,
    handler: () => void,
    primary = false,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = primary ? "btn btn-primary" : "btn";
    button.textContent = text;
    button.addEventListener("click", handler);
    return button;
  }
}
